import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { IconAdd, IconDelete, IconEmail, IconList } from '../../../../shared/icon/icon'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { useAlertRoutingAccess } from './access'
import { listTargets } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { ConnectionPill } from './connection-field'
import { NEW_TARGET, targetHref } from './paths'
import { TestOutcomeLine, useTargetActions } from './target-actions'
import { deleteBlocked, typeTitle } from './target-words'
import { SETUP_PILL, targetSetup } from './setup-words'

const typeLabel = type => (type ? type.charAt(0).toUpperCase() + type.slice(1) : '')

// Where a target sends, in words. A webhook's URL is a write-only secret, so
// only whether it is set up shows, and the host (eTLD+1) the API sends when it
// has one. A Connect type (slack_app) sends to "#channel · workspace": the
// channel's name the server stored, and its connection's workspace.
export const destinationsOf = (t, label = typeLabel(t.type), connect = Boolean(t.connection)) => {
  if (connect) {
    const channel = t.config && t.config.channel_name
    const workspace = t.connection && t.connection.externalName
    const parts = [channel && `#${channel}`, workspace].filter(Boolean)
    return parts.length ? parts.join(' · ') : `${label}: not set up`
  }
  if (t.type === 'email') return (t.addresses || []).length ? plural(t.addresses.length, 'address', 'addresses') : 'No addresses yet'
  if (!Object.values(t.secrets || {}).some(s => s && s.set)) return `${label}: not set up`
  return t.host ? `${label}: ${t.host}` : `${label}: set up`
}

// Email targets are staff inboxes and get what they get today (spec D25), so
// the switch only means something for the other channels.
export const personalText = t => (t.type === 'email' ? 'Personal data included (email)' : t.allowPersonal ? 'Personal data allowed' : 'No personal data')

// Names that would push the columns off a 1280 px window ellipsize at their
// column's cap (CLAUDE.md "Column widths"; the API keys lists' NAME_MAX).
export const TARGET_MAX = 260
export const DESTINATION_MAX = 220
export const DELIVERY_MAX = 190

// The cells' words on the row: the grid sorts, quick-filters and exports a
// column's field, so each holds what its cell shows.
export const targetRow = (t, labelOf, isConnect) => {
  const label = labelOf(t.type)
  return {
    ...t,
    typeText: label,
    scopeText: t.scopeType === 'platform' ? 'Platform' : t.organizationName || `Organization ${t.organizationID}`,
    destinationText: destinationsOf(t, label, isConnect(t.type)),
    connect: isConnect(t.type),
    personalText: personalText(t),
    lastSentText: t.lastSuccessAt ? `Sent ${formatAgo(t.lastSuccessAt)}` : 'Never sent',
    lastFailedText: t.lastFailureAt ? `Failed ${formatAgo(t.lastFailureAt)}${t.lastError ? `: ${t.lastError}` : ''}` : '',
    usedByText: plural(t.usedBy || 0, 'route'),
    rateText: t.ratePerMinute ? `${t.ratePerMinute} a minute` : ''
  }
}

// Two-line cells (the API keys lists): the target over its type and scope;
// where it sends over its personal data setting; its last delivery over its
// last failure; the routes that use it over its rate.
const targetCell = r => (r.data
  ? <span className="min-w-0">
      <span className="tb-cell-row">
        <span className="fw-semibold text-truncate" title={r.data.name}>{r.data.name}</span>
        {r.data.enabled === false && <Pill>Off</Pill>}
        {targetSetup(r.data) && <Pill tone="warn" title="Not on any route: only tests reach it.">{SETUP_PILL}</Pill>}
      </span>
      <span className="sw-cell-sub text-truncate" title={r.data.scopeText}><span>{r.data.typeText}</span> · <span>{r.data.scopeText}</span></span>
    </span>
  : '')
const destinationCell = r => {
  const t = r.data
  if (!t) return ''
  const down = t.connect && t.connection && t.connection.status !== 'connected'
  return (
    <span className="min-w-0">
      <span className="tb-cell-row"><span className="text-truncate" title={t.destinationText}>{t.destinationText}</span>{down && <ConnectionPill status={t.connection.status} tone="bad" />}</span>
      <span className="sw-cell-sub">{t.personalText}</span>
    </span>
  )
}
const deliveryCell = r => (r.data
  ? <span className="min-w-0">
      <span title={r.data.lastSuccessAt ? formatDateTime(r.data.lastSuccessAt) : undefined}>{r.data.lastSentText}</span>
      {r.data.lastFailedText && <span className="sw-cell-sub sw-tone-bad text-truncate" title={r.data.lastFailedText}>{r.data.lastFailedText}</span>}
    </span>
  : '')
const usedByCell = r => (r.data
  ? <span className="min-w-0">
      <span>{r.data.usedByText}</span>
      {r.data.rateText && <span className="sw-cell-sub">{r.data.rateText}</span>}
    </span>
  : '')

// Alert targets (/admin/alert-routing/targets; owner 2026-10-30: its own page
// in the sidebar). The house list page: the card's menu has one pinned New
// target (its page asks what kind first); a target no route uses says "Setup
// incomplete" beside its name (setup-words.js); a row opens the target's page
// and its pinned icons are Send test and Delete (the API keys lists' row
// actions; Delete held, with why on hover, while a route uses it). Send test's
// answer is the card's status line. The columns fit a 1280 px window: every
// fact is in a two-line cell (owner 2026-10-30: "Last success" was cut off),
// the single facts stay, hidden, in Table settings. The card sizes to its
// rows.
//
// The list reads again whenever the address changes, so a test's or a
// delete's effect shows at once.
export const TargetsPage = () => {
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const history = useHistory()
  const [rows, setRows] = useState(undefined)
  const [reload, setReload] = useState(0)
  const [said, setSaid] = useState('')
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  const { pathname } = useLocation()
  const actions = useTargetActions({ onDeleted: t => { setSaid(`${t.name} is deleted.`); setReload(n => n + 1) } })
  useEffect(() => {
    listTargets().then(v => { setError(null); setRows(v || []) }, err => { setRows(null); setError(serverMessage(err, "Targets couldn't be loaded."), err) })
  }, [pathname, reload, setError])
  const shown = useMemo(() => {
    if (!rows) return rows
    const typeOf = type => catalog.actionTypes.find(a => a.type === type)
    const labelOf = type => typeTitle(typeOf(type) || { type, label: typeLabel(type) })
    const isConnect = type => Boolean((typeOf(type) || {}).connect)
    return rows.map(t => targetRow(t, labelOf, isConnect))
  }, [rows, catalog.actionTypes])
  const gridList = useGridList(shown)

  const newTarget = canEdit ? <MenuItem key="new" Icon={IconAdd} text="New target" pin keepOnMobile show to={NEW_TARGET} /> : []
  const { sendTest, askDelete, testing } = actions
  const rowMenu = useMemo(() => t => (
    <Menu small>
      <MenuItem Icon={IconEmail} text="Send test" pin show={canEdit} disabled={Boolean(testing)} onClick={() => { setSaid(''); sendTest(t) }} />
      <MenuItem Icon={IconDelete} text="Delete" pin show={canEdit} disabled={(t.usedBy || 0) > 0} title={deleteBlocked(t.usedBy || 0) || undefined}
        onClick={() => { setSaid(''); askDelete(t) }} />
    </Menu>
  ), [canEdit, testing, sendTest, askDelete])

  const notice = testing || actions.outcome
    ? <TestOutcomeLine outcome={actions.outcome} testing={Boolean(testing)} named />
    : said ? <span className="sw-status" role="status">{said}</span> : null

  return (
    <FleetPage title="Alert targets" purpose="Where alerts are sent: email lists, Slack channels and webhooks.">
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
      {!error && <FleetGrid title="Targets" tableRef={tableRef} menu={newTarget} rows={(rows || []).length} rowHeight={ROW_HEIGHT_TWO_LINE} notice={notice}>
        <TableAgClient ref={tableRef} queryKey="targets" apiList={gridList} rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={t => history.push(targetHref(t.id))}
          emptyState={{ Icon: IconList, title: 'No targets', message: 'Add an email list, a Slack channel or a webhook.' }}>
          <ColumnFormat field="name" header="Target" sortable stacked flex={2} textMax={TARGET_MAX} render={targetCell} />
          <ColumnText field="typeText" header="Type" fit="content" sortable hide />
          <ColumnText field="scopeText" header="Scope" sortable hide />
          <ColumnFormat field="destinationText" header="Sends to" sortable stacked flex={1.5} textMax={DESTINATION_MAX} render={destinationCell} />
          <ColumnText field="personalText" header="Personal data" sortable hide />
          <ColumnFormat field="lastSentText" header="Last delivery" sortable={false} stacked textMax={DELIVERY_MAX} render={deliveryCell} />
          <ColumnFormat field="usedByText" header="Used by" sortable={false} stacked fit="content" render={usedByCell} />
          <ColumnText field="rateText" header="Rate" fit="content" sortable={false} hide />
          {canEdit && <ColumnMenu menu={rowMenu} />}
        </TableAgClient>
      </FleetGrid>}
      {actions.dialogs}
    </FleetPage>
  )
}
